import React, { useState, useEffect } from "react";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaSpinner, FaCheckCircle, FaExclamationCircle, FaMoon, FaSun, FaPalette } from "react-icons/fa";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from "react-router-dom";
import Logo from "../Assets/BooksCompl.png";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });
  const [colorTheme, setColorTheme] = useState(() => {
    return localStorage.getItem('colorTheme') || 'blue';
  });
  const navigate = useNavigate();

  const colorThemes = {
    blue: { name: 'Blue', primary: '#3b82f6' },
    orange: { name: 'Orange', primary: '#f97316' },
    purple: { name: 'Purple', primary: '#a855f7' }
  };

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('theme-blue', 'theme-orange', 'theme-purple');
    root.classList.add(`theme-${colorTheme}`);
    localStorage.setItem('colorTheme', colorTheme);
  }, [colorTheme]);

  // Check for impersonation data in URL or existing authentication
  useEffect(() => {
    const checkAuth = async () => {
      // Check if this is an impersonation redirect
      const urlParams = new URLSearchParams(window.location.search);
      const impersonateData = urlParams.get('impersonate');
      
      if (impersonateData) {
        try {
          // Decode and parse the session data
          const sessionData = JSON.parse(decodeURIComponent(impersonateData));
          
          // Store user token and info in User Dashboard's localStorage
          localStorage.setItem("token", sessionData.token);
          localStorage.setItem("userEmail", sessionData.userEmail);
          localStorage.setItem("userName", sessionData.userName);
          localStorage.setItem("isImpersonating", "true");
          localStorage.setItem("adminSession", JSON.stringify(sessionData.adminSession));
          
          // Clean URL and redirect to dashboard
          window.history.replaceState({}, document.title, "/dashboard");
          navigate("/dashboard");
          return;
        } catch (error) {
          console.error("Error processing impersonation data:", error);
        }
      }
      
      // Check if user is already authenticated
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.user?.isBookkeepingCollaborator || data.user?.isAssignee) {
              navigate("/bookkeeping");
            } else {
              navigate("/dashboard");
            }
          }
        } catch (error) {
          // Token is invalid, do nothing and show login page
          console.error("Token validation failed:", error);
        }
      }
    };
    
    checkAuth();
  }, [navigate]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);
  
  const cycleColorTheme = () => {
    const themes = Object.keys(colorThemes);
    const currentIndex = themes.indexOf(colorTheme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setColorTheme(themes[nextIndex]);
  };

  const handleOAuthLogin = async (_provider, token) => {
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed.");
      }

      const data = await response.json();
      localStorage.setItem("token", data.token);
      setMessage("Login successful! Redirecting...");
      
      // Redirect to appropriate page based on unlocked services
      setTimeout(() => {
        redirectAfterLogin(data.user);
      }, 1000);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const redirectAfterLogin = (user) => {
    if (user?.isBookkeepingCollaborator || user?.isAssignee) {
      navigate("/bookkeeping");
      return;
    }
    if (user.isUSAEnabled && user.isTaxFilingEnabled &&
        user.isBookkeepingEnabled && user.isCFOEnabled) {
      navigate("/tax-filing");
    } else if (user.isUSAEnabled) {
      navigate("/usa-company-formation");
    } else if (user.isTaxFilingEnabled) {
      navigate("/tax-filing");
    } else if (user.isBookkeepingEnabled) {
      navigate("/bookkeeping");
    } else if (user.isCFOEnabled) {
      navigate("/cfo-advisory");
    } else {
      navigate("/pricing");
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed.");
      }

      const data = await response.json();
      localStorage.setItem("token", data.token);
      localStorage.setItem("userId", data.user._id);
      setMessage("Login successful! Redirecting...");
      
      setTimeout(() => {
        redirectAfterLogin(data.user);
      }, 1000);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId="383385845506-jknb7cqrrmbmhnki2vbdkindqdpmpce8.apps.googleusercontent.com">
      <div className="min-h-screen flex bg-gray-50 dark:bg-dark-900 transition-colors duration-300">
        {/* Theme Toggle Buttons */}
        <div className="fixed top-6 right-6 z-50 flex gap-2">
          <button
            onClick={cycleColorTheme}
            className="p-3 rounded-xl bg-white dark:bg-dark-800 shadow-lg border border-gray-200 dark:border-dark-700 hover:scale-105 transition-all duration-200 group"
            aria-label="Change color theme"
          >
            <FaPalette className="w-5 h-5 transition-transform group-hover:rotate-12" style={{ color: colorThemes[colorTheme]?.primary }} />
          </button>
          <button
            onClick={toggleTheme}
            className="p-3 rounded-xl bg-white dark:bg-dark-800 shadow-lg border border-gray-200 dark:border-dark-700 hover:scale-105 transition-all duration-200"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? (
              <FaSun className="w-5 h-5" style={{ color: 'var(--accent-500)' }} />
            ) : (
              <FaMoon className="w-5 h-5" style={{ color: 'var(--primary-600)' }} />
            )}
          </button>
        </div>

        {/* Left Side - Branding & Image */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: `linear-gradient(135deg, var(--primary-600), var(--primary-700), var(--primary-900))` }}>
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-500/30 rounded-full blur-3xl animate-pulse-slow" />
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400/30 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
            
            {/* 3D Floating Elements */}
            <div className="absolute top-20 left-20 w-20 h-20 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-2xl opacity-20 animate-float rotate-12" style={{ animationDelay: '0s' }} />
            <div className="absolute bottom-32 right-20 w-16 h-16 bg-gradient-to-br from-primary-300 to-primary-500 rounded-xl opacity-20 animate-float" style={{ animationDelay: '2s' }} />
            <div className="absolute top-1/3 right-1/4 w-12 h-12 bg-gradient-to-br from-white to-gray-200 rounded-lg opacity-10 animate-float" style={{ animationDelay: '1s' }} />
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
            <img src={Logo} alt="BooksComply" className="h-16 w-auto mb-8 drop-shadow-lg" />
            <h2 className="text-4xl font-bold mb-4 text-center">Welcome to BooksComply</h2>
            <p className="text-lg text-white/80 text-center max-w-md">
              Your trusted partner for accounting, tax filing, and business compliance services.
            </p>
            
            {/* Feature List */}
            <div className="mt-12 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <FaCheckCircle className="w-5 h-5" />
                </div>
                <span className="text-white/90">Professional Tax Filing Services</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <FaCheckCircle className="w-5 h-5" />
                </div>
                <span className="text-white/90">Expert Bookkeeping Solutions</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <FaCheckCircle className="w-5 h-5" />
                </div>
                <span className="text-white/90">USA Company Formation</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md animate-scale-in">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <img src={Logo} alt="BooksComply" className="h-12 w-auto mx-auto" />
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-dark-800 dark:text-white mb-2">Welcome Back</h1>
              <p className="text-gray-600 dark:text-gray-400">Login to your account to continue</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleEmailLogin} className="space-y-5">
              {/* Email Input */}
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaEnvelope className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                </div>
                <input
                  type="email"
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {/* Password Input */}
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaLock className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-primary-500 transition-colors"
                >
                  {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                </button>
              </div>

              {/* Forgot Password Link */}
              <div className="text-right">
                <Link 
                  to="/forgetpassword" 
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl font-semibold text-white shadow-lg active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ 
                  background: `linear-gradient(135deg, var(--primary-600), var(--primary-500))`,
                  boxShadow: `0 10px 15px -3px var(--glow-color)`
                }}
              >
                {loading ? (
                  <>
                    <FaSpinner className="w-5 h-5 animate-spin" />
                    Logging in...
                  </>
                ) : "Login"}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-dark-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gray-50 dark:bg-dark-900 text-gray-500">or continue with</span>
              </div>
            </div>

            {/* Google Login */}
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={(credentialResponse) => {
                  try {
                    const decoded = jwtDecode(credentialResponse.credential);
                    console.log("Decoded JWT:", decoded);
                    handleOAuthLogin("Google", credentialResponse.credential);
                  } catch (error) {
                    console.error("Error decoding token:", error);
                    setMessage("Failed to decode token. Please try again.");
                  }
                }}
                onError={() => {
                  console.log("Google Login Failed");
                  setMessage("Google login failed. Please try again.");
                }}
                theme={isDarkMode ? "filled_black" : "outline"}
                size="large"
                shape="pill"
              />
            </div>

            {/* Sign Up Link */}
            <p className="mt-6 text-center text-gray-600 dark:text-gray-400">
              Don't have an account?{" "}
              <Link to="/signup" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                Sign Up
              </Link>
            </p>

            {/* Message Alert */}
            {message && (
              <div className={`mt-4 p-4 rounded-xl flex items-center gap-3 animate-slide-up ${
                message.includes("successful") 
                  ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400" 
                  : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400"
              }`}>
                {message.includes("successful") 
                  ? <FaCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  : <FaExclamationCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                }
                <span className="text-sm font-medium">{message}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </GoogleOAuthProvider> 
  );
};

export default LoginPage;